/**
 * ModuleList component with drag-and-drop reordering functionality
 * Uses @dnd-kit for modern drag and drop support
 */

import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Edit,
  Trash2,
  Video,
  Headphones,
  FileText,
  BookOpen,
  HelpCircle,
  ArrowUp,
  ArrowDown,
  Plus,
} from 'lucide-react';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { Module, ModuleType } from '../../types/course';

interface ModuleListProps {
  modules: Module[];
  onModulesReorder: (newModules: Module[]) => void;
  onModuleEdit: (module: Module) => void;
  onModuleDelete: (moduleId: string) => void;
  onAddModule: () => void;
  onInsertModule: (position: number) => void;
  isLoading?: boolean;
}

interface SortableModuleItemProps {
  module: Module;
  index: number;
  onEdit: (module: Module) => void;
  onDelete: (moduleId: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onInsertAbove: (position: number) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

/**
 * Get module type icon
 */
const getModuleTypeIcon = (type: ModuleType) => {
  switch (type) {
    case ModuleType.VIDEO:
      return <Video className="w-4 h-4" />;
    case ModuleType.AUDIO:
      return <Headphones className="w-4 h-4" />;
    case ModuleType.PDF:
      return <FileText className="w-4 h-4" />;
    case ModuleType.TEXT:
      return <BookOpen className="w-4 h-4" />;
    case ModuleType.QUIZ:
      return <HelpCircle className="w-4 h-4" />;
    default:
      return <BookOpen className="w-4 h-4" />;
  }
};

/**
 * Sortable module item component
 */
const SortableModuleItem: React.FC<SortableModuleItemProps> = ({
  module,
  index,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onInsertAbove,
  canMoveUp,
  canMoveDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {/* Insert button above this module */}
      <div className="flex justify-center mb-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onInsertAbove(index)}
          className="text-xs text-neutral-500 hover:text-primary border-dashed border border-neutral-300 hover:border-primary"
        >
          <Plus className="w-3 h-3 mr-1" />
          Insert Module Here
        </Button>
      </div>

      <Card className="p-4 mb-4 border border-neutral-200 hover:border-neutral-300 transition-colors">
        <div className="flex items-center gap-3">
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-neutral-400 hover:text-neutral-600"
          >
            <GripVertical className="w-4 h-4" />
          </div>

          {/* Module order number */}
          <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
            {index + 1}
          </div>

          {/* Module type icon */}
          <div className="flex-shrink-0 text-neutral-600">
            {getModuleTypeIcon(module.type)}
          </div>

          {/* Module info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-neutral-900 truncate">
              {module.title}
            </h4>
            <p className="text-sm text-neutral-600 truncate">
              {module.description}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs bg-neutral-100 text-neutral-700 px-2 py-1 rounded">
                {module.type.toUpperCase()}
              </span>
              <span className="text-xs text-neutral-500">
                {module.estimatedDuration} min
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            {/* Move up/down buttons */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveUp(index)}
              disabled={!canMoveUp}
              className="p-1"
              title="Move up"
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveDown(index)}
              disabled={!canMoveDown}
              className="p-1"
              title="Move down"
            >
              <ArrowDown className="w-4 h-4" />
            </Button>

            {/* Edit button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onEdit(module)}
              className="p-1 text-blue-600 hover:text-blue-700"
              title="Edit module"
            >
              <Edit className="w-4 h-4" />
            </Button>

            {/* Delete button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDelete(module.id)}
              className="p-1 text-red-600 hover:text-red-700"
              title="Delete module"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

/**
 * ModuleList component with drag-and-drop functionality
 */
export const ModuleList: React.FC<ModuleListProps> = ({
  modules,
  onModulesReorder,
  onModuleEdit,
  onModuleDelete,
  onAddModule,
  onInsertModule,
  isLoading = false,
}) => {
  const [localModules, setLocalModules] = useState(modules);

  // Update local modules when props change
  React.useEffect(() => {
    setLocalModules(modules);
  }, [modules]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
   * Handle drag end event
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = localModules.findIndex(m => m.id === active.id);
      const newIndex = localModules.findIndex(m => m.id === over?.id);

      const newModules = arrayMove(localModules, oldIndex, newIndex);
      
      // Update order field for each module
      const reorderedModules = newModules.map((module, index) => ({
        ...module,
        order: index,
      }));

      setLocalModules(reorderedModules);
      onModulesReorder(reorderedModules);
    }
  };

  /**
   * Handle move up
   */
  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newModules = arrayMove(localModules, index, index - 1);
      const reorderedModules = newModules.map((module, idx) => ({
        ...module,
        order: idx,
      }));
      setLocalModules(reorderedModules);
      onModulesReorder(reorderedModules);
    }
  };

  /**
   * Handle move down
   */
  const handleMoveDown = (index: number) => {
    if (index < localModules.length - 1) {
      const newModules = arrayMove(localModules, index, index + 1);
      const reorderedModules = newModules.map((module, idx) => ({
        ...module,
        order: idx,
      }));
      setLocalModules(reorderedModules);
      onModulesReorder(reorderedModules);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="p-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-neutral-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (localModules.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          No modules yet
        </h3>
        <p className="text-neutral-600 mb-6">
          Start building your course by adding your first module.
        </p>
        <Button onClick={onAddModule} type="button">
          <Plus className="w-4 h-4 mr-2" />
          Add First Module
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localModules.map(m => m.id)}
          strategy={verticalListSortingStrategy}
        >
          {localModules.map((module, index) => (
            <SortableModuleItem
              key={module.id}
              module={module}
              index={index}
              onEdit={onModuleEdit}
              onDelete={onModuleDelete}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onInsertAbove={onInsertModule}
              canMoveUp={index > 0}
              canMoveDown={index < localModules.length - 1}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Add module button at the end */}
      <div className="flex justify-center pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={onAddModule}
          className="border-dashed border border-neutral-300 hover:border-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Module at End
        </Button>
      </div>
    </div>
  );
};

